
import { Component, Input, OnChanges, ChangeDetectorRef } from '@angular/core';

import {
    IServicesTableConfig,
    IServicesTableGroups,
    IServicesTableColumns,
    TServicesTableData
} from './IServicesTableConfig';

import { faInfoCircle, faAngleDown, faArrowRight, faCheck, faTimes, IconDefinition } from "@fortawesome/free-solid-svg-icons";

interface IGroupParams {
    show: boolean;
}

interface IColumnParams {
    showTranslation: boolean;
}

@Component({
    selector: 'services-table',
    templateUrl: 'services-table.component.html',
    styleUrls: ['./services-table.component.scss']
})

export class ServicesTableComponent implements OnChanges {

    @Input()
    public tableConfig: IServicesTableConfig;

    public manageableTableConfig: IServicesTableConfig;

    public columnsDef: string[];
    public groups: string[];

    public groupsParams: { [key: string]: IGroupParams };
    public columnParams: { [key: string]: IColumnParams };

    public detailsIndex: number[] = [];

    public iconAngleDown: IconDefinition = faAngleDown;
    public iconAngleRight: IconDefinition = faArrowRight;
    public iconCheck: IconDefinition = faCheck;
    public iconTimes: IconDefinition = faTimes;
    public iconInfoCircle: IconDefinition = faInfoCircle;

    constructor(
        private readonly changeDetectorRefs: ChangeDetectorRef,
    ) { }

    public ngOnChanges(): void {
        if (this.tableConfig) {
            this.groups = this.tableConfig.groups.map((group: IServicesTableGroups) => group.name);
            this.initUiParams();
            this.refreshTableReferences();
        }
    }

    public toggleDetails(index: number): void {
        if (this.detailsIsOpen(index)) {
            const i: number = this.detailsIndex.indexOf(index);
            this.detailsIndex.splice(i, 1);
        } else {
            this.detailsIndex.push(index);
        }
    }

    public detailsIsOpen(index: number): boolean {
        return this.detailsIndex.includes(index);
    }

    public toggleGroup(groupName: string): void {
        this.groupsParams[groupName].show = !this.groupsParams[groupName].show;
        this.refreshTableReferences();
    }

    public toggleCodeTranslation(columnDef: string): void {
        this.columnParams = this.getColumnParams({
            columnDef,
            value: this.columnParams[columnDef].showTranslation
        });
    }

    public getGroupName(groupName: string): string {
        const groupColumnsLength: number = this.tableConfig.groups.find((group: IServicesTableGroups) => group.name === groupName).columns.length;
        return this.groupsParams[groupName].show ?
            groupName :
            `${groupName} (${groupColumnsLength})`;
    }

    public isString(data: boolean | string): boolean {
        return typeof data === 'string';
    }

    private refreshTableReferences(): void {
        const groupsToHide: string[] = Object.keys(this.groupsParams).filter(
            (groupName: string) => !this.groupsParams[groupName].show
        );
        let servicesToShow: string[] = [];
        const groups: IServicesTableGroups[] = this.tableConfig.groups.map(
            (group: IServicesTableGroups, index: number) => {
                if (groupsToHide.some((service: string) => service === group.name)) {
                    // Why `emptyGroup_${index}` ?
                    // TR_2019_04_24 : when a group is colapsed a single column is required in order to keep the abiliity to access to the group toggle button
                    // => thereby an empty column name is generated based on the index of the group
                    const emptyGroupName: string = `emptyGroup_${index}`;
                    servicesToShow = [...servicesToShow, emptyGroupName];
                    return {
                        ...group,
                        columns: [{
                            columnDef: emptyGroupName,
                            header: ""
                        }]
                    };
                }
                const columnDef: string[] = group.columns.map((column: IServicesTableColumns): string => column.columnDef)
                    .filter((column: string) => column);
                servicesToShow = [...servicesToShow, ...columnDef];
                return group;
            }
        );

        const data: TServicesTableData[] = this.tableConfig.data.map(
            (dataItem: TServicesTableData) => {
                const element: TServicesTableData = new Map();
                servicesToShow.forEach((key: string) => {
                    if (dataItem.has(key)) {
                        element.set(key, dataItem.get(key));
                    }
                });
                return element;
            }
        );

        this.manageableTableConfig = {
            groups,
            data
        };
        this.columnsDef = this.manageableTableConfig.data[0] ? Array.from(this.manageableTableConfig.data[0].keys()) : [];
        this.changeDetectorRefs.detectChanges();
    }

    private initUiParams(): void {
        this.groupsParams = this.tableConfig.groups.reduce((acc: { [key: string]: IGroupParams }, group: IServicesTableGroups) => ({
            ...acc,
            [group.name]: {
                show: true
            }
        }), {});
        this.columnParams = this.getColumnParams();
    }

    private getColumnParams(columnChange: {
        columnDef: string;
        value: boolean;
    } = null): { [key: string]: IColumnParams } {
        return this.tableConfig.groups.reduce((acc: { [key: string]: IGroupParams }, group: IServicesTableGroups) => ({
            ...acc,
            ...group.columns.reduce((columnAcc: { [key: string]: IGroupParams }, column: IServicesTableColumns) => ({
                ...columnAcc,
                [column.columnDef]: {
                    showTranslation: columnChange && columnChange.columnDef === column.columnDef ?
                        !columnChange.value :
                        false
                }
            }), {})
        }), {}) as unknown as { [key: string]: IColumnParams }; // TODO NG UPGRADE as unknown as ...
    }
}

